import type { BetterstartIntegrationId } from '@cli-types/integration-engine/integrations.js'
import type {
  RemoveIntegrationsOptions,
  RemoveIntegrationsResult
} from '@cli-types/next-adapter/runtime.js'
import { removeEnvVars } from '@cli-utils/files.js'
import { writeConfigFile } from '@core-engine/config/serialize/write-config-file.js'
import { flattenEnvKeys } from '@core-engine/installable/protocol/flatten-env-keys.js'
import { integrationManifestStore } from '@integration-engine/manifests/integration-manifest-store.js'
import { removeDependenciesAsync } from '@next-adapter/init/scaffolders/dependencies/remove-dependencies-async.js'
import { getIntegrationDefinition } from '@next-adapter/integration-registry.js'
import { findBlockingIntegrationDependencies } from './find-blocking-integration-dependencies.js'
import { integrationFiles } from './integration-files.js'
import { normalizeInstalledIntegrations } from './normalize-installed-integrations.js'
import { removeEmailTemplatesDirIfEmpty } from './remove-email-templates-dir-if-empty.js'
import { writeCoreProviderIndexes } from './write-core-provider-indexes.js'

export async function removeIntegrations({
  cwd,
  config,
  pm,
  integrationIds
}: RemoveIntegrationsOptions): Promise<RemoveIntegrationsResult> {
  const installedIntegrations = normalizeInstalledIntegrations(config)
  const removed: BetterstartIntegrationId[] = []
  const warnings: string[] = []

  for (const integrationId of integrationIds) {
    if (!installedIntegrations.includes(integrationId)) {
      warnings.push(`${integrationId} is not installed.`)
      continue
    }

    const manifest = integrationManifestStore.load(cwd, integrationId)
    if (!manifest) {
      throw new Error(`Missing manifest for installed integration "${integrationId}".`)
    }

    const dependents = installedIntegrations.filter((installedIntegrationId) => {
      if (installedIntegrationId === integrationId) {
        return false
      }

      return getIntegrationDefinition(installedIntegrationId).dependencies.includes(integrationId)
    })

    if (dependents.length > 0) {
      throw new Error(
        `Cannot remove ${integrationId}. The following integrations depend on it: ${dependents.join(', ')}`
      )
    }

    const blockers = findBlockingIntegrationDependencies(cwd, config, integrationId)
    if (blockers.length > 0) {
      throw new Error(`Cannot remove ${integrationId}:\n- ${blockers.join('\n- ')}`)
    }

    const deletedPaths: string[] = []

    for (const filePath of manifest.ownedFiles) {
      const relativeOutputPath = filePath.replace(`${config.paths.admin.replace(/^\.\//, '')}/`, '')
      const deletedPath = integrationFiles.restoreOrDeleteFile(cwd, config, relativeOutputPath)
      if (deletedPath) {
        deletedPaths.push(deletedPath)
      }
    }

    integrationManifestStore.remove(cwd, integrationId)
    const nextInstalledIntegrations = installedIntegrations.filter(
      (installedIntegrationId) => installedIntegrationId !== integrationId
    )
    installedIntegrations.splice(0, installedIntegrations.length, ...nextInstalledIntegrations)
    config.integrations.installed = [...nextInstalledIntegrations].sort()

    const definition = getIntegrationDefinition(integrationId)
    if (definition.capabilities?.emailProvider?.provider === config.email.provider) {
      config.email.provider = 'none'
    }
    if (definition.capabilities?.storageProvider?.provider === config.storage.provider) {
      config.storage.provider = 'local'
    }
    writeCoreProviderIndexes(cwd, config)

    const remainingIntegrations = normalizeInstalledIntegrations(config)
    const hasRemainingEmailProvider = remainingIntegrations.some((remainingIntegrationId) =>
      Boolean(getIntegrationDefinition(remainingIntegrationId).capabilities?.emailProvider)
    )
    if (definition.capabilities?.emailProvider && !hasRemainingEmailProvider) {
      removeEmailTemplatesDirIfEmpty(cwd, config)
    }

    const dependencyDiff = integrationFiles.getUnusedDependencies(
      remainingIntegrations,
      integrationId,
      config.presets.installed
    )

    if (dependencyDiff.dependencies.length > 0 || dependencyDiff.devDependencies.length > 0) {
      const result = await removeDependenciesAsync({
        cwd,
        pm,
        dependencies: dependencyDiff.dependencies,
        devDependencies: dependencyDiff.devDependencies
      })

      if (!result.success) {
        warnings.push(result.error ?? `Failed to remove dependencies for ${integrationId}.`)
      }
    }

    const usedEnvKeys = new Set(
      remainingIntegrations.flatMap((remainingIntegrationId) =>
        flattenEnvKeys(getIntegrationDefinition(remainingIntegrationId))
      )
    )
    const removableEnvKeys = manifest.envKeys.filter((envKey) => !usedEnvKeys.has(envKey))
    if (removableEnvKeys.length > 0) {
      removeEnvVars(cwd, removableEnvKeys)
    }

    integrationFiles.cleanupEmptyDirs(cwd, deletedPaths, config)

    writeConfigFile(cwd, config)
    removed.push(integrationId)
  }

  return {
    removed,
    warnings,
    config
  }
}
